import { Button, Drawer, Modal, Popover, message } from "antd";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useDispatch } from "react-redux";
import { isEmpty } from "lodash";
import {
  GET_AI_RESPONSE,
  GET_AI_SUGGESTION_QUICK_TIP,
  GET_QUICK_TIPS_PREFERENCE,
} from "../../../../graphql/query";
import {
  CREATE_AI_SUGGESTION,
  DELETE_AI_SUGGESTION,
  SAVE_QUICK_TIPS_PREFERENCE,
  UPDATE_AI_SUGGESTION,
} from "../../../../graphql/mutation/mutation";
import { ADDING_TYPE } from "../../../adminDashboard/settings/CustomizeAiSuggestions";
import {
  setTextAiDraftContent,
  setTextAiResponseHistoryId,
} from "../../../../redux/slices/aiSlice";
import CustomItemForm from "./CustomItemForm";
import HideUnhidePanel from "./HideUnhidePanel";
import CustomizePanelContent from "./CustomizePanelContent";
import { AI_RESPONSE_COPY, COMMUNICATION_CHANNEL, SUGGESTION_TYPE } from "./constants";

const TYPE_DELAY_MS = 20;

// CustomItemForm channel toggles { text, email } <-> backend communicationChannels []
const channelsToArray = ({ text, email } = {}) =>
  [text && COMMUNICATION_CHANNEL.TEXT, email && COMMUNICATION_CHANNEL.EMAIL].filter(Boolean);
const channelsToObject = (arr = []) => ({
  // Default an item with no saved channels to both, matching the add-form default.
  text: arr.length ? arr.includes(COMMUNICATION_CHANNEL.TEXT) : true,
  email: arr.length ? arr.includes(COMMUNICATION_CHANNEL.EMAIL) : true,
});

// Feature flag — user-level custom tips/tones. ON now that the backend exposes
// `userId` on getAiSuggestionQuickTip and scopes custom rows to the auth user.
// Drives the per-item edit/delete menu + 👤 marker.
const USER_CUSTOM_TIPS_ENABLED = true;

// An item is the logged-in user's own custom tip/tone ONLY when the backend
// stamped it with their id. Admin (company/location/department) items come back
// with `userId === null` and are read-only — no more-options menu for them.
const isOwnCustom = (item, userId) =>
  USER_CUSTOM_TIPS_ENABLED && item?.userId != null && String(item.userId) === String(userId);

// Visible unless the item's id is in the saved `hidden` set. Newly created
// items aren't in `hidden`, so they're visible by default.
const isVisible = (item, hidden) => !(hidden || []).includes(item.id);

/**
 * "Customize AI response" controller (EAI-1497 refinement).
 *
 * Owns ALL data + selection logic and renders the SAME `CustomizePanelContent`
 * inside two interchangeable containers:
 *   - a compact anchored **Popover** (default), and
 *   - a right-side **Drawer** (the "maximize" view).
 * State lives here, so maximizing/restoring preserves tips, tone, unsaved
 * changes and the save-preference toggle with no reset.
 *
 * The trigger (composer chevron) is supplied by the host via `renderTrigger`
 * so each surface keeps its own button styling; the Popover anchors to it.
 *
 * @param {{
 *   user: object,
 *   locationId?: number,
 *   applicationUseCaseId?: number,
 *   quickTipVariables: object,
 *   buildResponsePayload: (promptData: object) => object,
 *   text?: object, setInputText?: Function, setLoader?: Function,
 *   value: object, onChange: Function,
 *   emptyTipsText?: string, emptyTonesText?: string,
 *   renderTrigger: (state: { open: boolean }) => React.ReactNode,
 * }} props
 */
export default function CustomizeAiResponse({
  user,
  locationId,
  applicationUseCaseId,
  quickTipVariables,
  buildResponsePayload,
  text,
  setInputText,
  setLoader,
  value,
  onChange,
  emptyTipsText,
  emptyTonesText,
  channelSelectable,
  renderTrigger,
}) {
  const dispatch = useDispatch();
  const userId = user?.id;
  const companyId = user?.company?.id;

  const [open, setOpen] = useState(false); // popover
  const [maximized, setMaximized] = useState(false); // drawer
  const [hideMode, setHideMode] = useState(false); // inline hide/unhide view
  const [shownTipIds, setShownTipIds] = useState([]); // hide/unhide draft
  const [shownToneIds, setShownToneIds] = useState([]);
  const [savePref, setSavePref] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false); // Generate action only
  const [form, setForm] = useState(null); // { mode, suggestionType, id, initialValue }
  const [pendingDelete, setPendingDelete] = useState(null); // item awaiting delete confirm
  const [deleting, setDeleting] = useState(false);
  const [savingForm, setSavingForm] = useState(false); // Add/Edit form submit in flight
  const active = open || maximized;

  // Custom tips/tones are scoped to the currently selected opportunity-type tab
  // (Sales / Service / AutoSavvy …), surfaced via quickTipVariables.rootOppTypeId.
  const opportunityTypeId = quickTipVariables?.rootOppTypeId ?? null;

  const {
    data: tipsData,
    loading: quickTipLoader,
    refetch: refetchTips,
  } = useQuery(GET_AI_SUGGESTION_QUICK_TIP, { skip: !active, variables: quickTipVariables });
  // Normalize ids to numbers up front: getAiSuggestionQuickTip returns string
  // ids while getQuickTipsPreference (hidden/preserved) returns Int[], so
  // comparisons and the saveQuickTipsPreference payload must all be numeric.
  const allItems = (tipsData?.getAiSuggestionQuickTip || []).map(t => ({ ...t, id: Number(t.id) }));
  const dynamicTips = allItems.filter(t => t?.isActive && t?.suggestionType === ADDING_TYPE.TIPS);
  const dynamicTones = allItems.filter(t => t?.isActive && t?.suggestionType === ADDING_TYPE.TONES);
  const additionalCommentsEnabled = allItems.some(
    t => t?.isActive && t?.suggestionType === ADDING_TYPE.ADDITIONAL_NOTES
  );

  const {
    data: prefData,
    loading: prefLoading,
    refetch: refetchPref,
  } = useQuery(GET_QUICK_TIPS_PREFERENCE, {
    // Both keys are required to read the right bucket — without the use case id
    // the message and review surfaces would share one preference record.
    skip: !active || !locationId || !applicationUseCaseId,
    variables: { locationId, applicationUseCaseId: Number(applicationUseCaseId) },
  });
  const pref = prefData?.getQuickTipsPreference;
  const toNums = arr => (arr || []).map(Number);
  // `hidden` = ids the user chose to hide (empty / absent → nothing hidden).
  const tipHidden = toNums(pref?.quickTips?.hidden);
  const tipPreserved = toNums(pref?.quickTips?.preserved);
  const toneHidden = toNums(pref?.quickTones?.hidden);
  const tonePreserved = toNums(pref?.quickTones?.preserved);

  const [saveQuickTipsPreference, { loading: savingPref }] = useMutation(
    SAVE_QUICK_TIPS_PREFERENCE
  );
  const [createSuggestion] = useMutation(CREATE_AI_SUGGESTION);
  const [updateSuggestion] = useMutation(UPDATE_AI_SUGGESTION);
  const [deleteSuggestion] = useMutation(DELETE_AI_SUGGESTION);
  const [getAiResponse] = useLazyQuery(GET_AI_RESPONSE, {
    nextFetchPolicy: "network-only",
  });

  const visibleTips = dynamicTips.filter(t => isVisible(t, tipHidden));
  const visibleTones = dynamicTones.filter(t => isVisible(t, toneHidden));

  // The "Save these preferences" checkbox stays hidden while the current
  // selection still matches the saved defaults (the `preserved` sets that
  // `saveCurrentAsDefault` persists, and that the panel seeds from on open).
  // It only appears once the user changes their tip/tone selection away from
  // those defaults — there is nothing new to save until then.
  const sameIdSet = (a = [], b = []) => {
    if (a.length !== b.length) return false;
    const setB = new Set(b);
    return a.every(id => setB.has(id));
  };
  const selectionMatchesDefault =
    sameIdSet(value.quickTipIds, tipPreserved) && sameIdSet(value.toneIds, tonePreserved);
  const showSavePref = !selectionMatchesDefault;

  // If the user reverts back to the defaults, the checkbox disappears — clear a
  // stale "checked" state so a hidden toggle can't silently save on Generate.
  useEffect(() => {
    if (!showSavePref && savePref) setSavePref(false);
  }, [showSavePref, savePref]);

  // Reset the "save" toggle and seed the selection from saved defaults each time
  // the panel opens with an empty selection. Keyed on `active` so switching
  // between popover and drawer does NOT re-seed (state is preserved).
  const seededRef = useRef(false);
  useEffect(() => {
    if (!active) {
      seededRef.current = false;
      return;
    }
    if (seededRef.current || prefLoading) return;
    seededRef.current = true;
    setSavePref(false);
    const empty = !value.quickTipIds.length && !value.toneIds.length;
    if (empty && (tipPreserved.length || tonePreserved.length)) {
      onChange({ ...value, quickTipIds: [...tipPreserved], toneIds: [...tonePreserved] });
    }
    // value/onChange intentionally omitted — seed runs once per open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, prefLoading, tipPreserved, tonePreserved]);

  const closeAll = () => {
    setOpen(false);
    setMaximized(false);
    setHideMode(false);
  };
  const toggleMaximize = () =>
    setMaximized(prev => {
      const next = !prev;
      setOpen(!next); // popover only when not maximized
      return next;
    });

  // Enter the inline hide/unhide view. The panel works in "shown" terms, so seed
  // the shown set as every item that isn't currently hidden.
  const enterHideMode = () => {
    setShownTipIds(dynamicTips.filter(t => !tipHidden.includes(t.id)).map(t => t.id));
    setShownToneIds(dynamicTones.filter(t => !toneHidden.includes(t.id)).map(t => t.id));
    setHideMode(true);
  };
  const toggleShownTip = id =>
    setShownTipIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  const toggleShownTone = id =>
    setShownToneIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const toggleTip = item => {
    const has = value.quickTipIds.includes(item.id);
    onChange({
      ...value,
      quickTipIds: has
        ? value.quickTipIds.filter(id => id !== item.id)
        : [...value.quickTipIds, item.id],
    });
  };
  const toggleTone = item =>
    onChange({ ...value, toneIds: value.toneIds.includes(item.id) ? [] : [item.id] });

  const textOf = (ids, list) => list.filter(it => ids.includes(it.id)).map(it => it.tip);

  const persistPreference = ({
    tipHiddenIds,
    toneHiddenIds,
    tipPreservedIds,
    tonePreservedIds,
  }) => {
    // Both scope keys are mandatory server-side; bail with a readable message
    // instead of firing a mutation with a null/NaN scope.
    if (!locationId || !applicationUseCaseId) {
      throw new Error("Couldn't save preferences — no active location.");
    }
    return saveQuickTipsPreference({
      variables: {
        locationId,
        applicationUseCaseId: Number(applicationUseCaseId),
        quickTips: { hidden: toNums(tipHiddenIds), preserved: toNums(tipPreservedIds) },
        quickTones: { hidden: toNums(toneHiddenIds), preserved: toNums(tonePreservedIds) },
      },
    });
  };

  const saveCurrentAsDefault = () =>
    persistPreference({
      // Keep the existing hidden set; only the default selection changes here.
      tipHiddenIds: tipHidden,
      toneHiddenIds: toneHidden,
      tipPreservedIds: value.quickTipIds,
      tonePreservedIds: value.toneIds,
    });

  // Persist the current selection as the default the moment the user ticks
  // "Save these preferences for future responses" — Generate no longer saves.
  // Unchecking is a no-op (there is no un-save). After a successful save the
  // selection matches the refreshed defaults, so `showSavePref` hides the
  // checkbox on its own.
  const handleSavePrefToggle = async checked => {
    setSavePref(checked);
    if (!checked) return;
    try {
      await saveCurrentAsDefault();
      await refetchPref();
      message.success(AI_RESPONSE_COPY.SAVED_TOAST);
    } catch (err) {
      setSavePref(false);
      message.error(err?.message || "Failed to save preferences");
    }
  };

  // Save the hide/unhide selection: the panel gives us the shown sets, so the
  // hidden set is every item NOT shown. Keep the existing `preserved` defaults,
  // then exit the hide/unhide view.
  const handleSaveVisibility = async () => {
    try {
      await persistPreference({
        tipHiddenIds: dynamicTips.filter(t => !shownTipIds.includes(t.id)).map(t => t.id),
        toneHiddenIds: dynamicTones.filter(t => !shownToneIds.includes(t.id)).map(t => t.id),
        tipPreservedIds: tipPreserved,
        tonePreservedIds: tonePreserved,
      });
      await refetchPref();
      setHideMode(false);
      message.success("Visibility updated");
    } catch (err) {
      message.error(err?.message || "Failed to update visibility");
    }
  };

  const openAdd = suggestionType => setForm({ mode: "add", suggestionType });
  const openEdit = item =>
    setForm({
      mode: "edit",
      suggestionType: item.suggestionType,
      id: item.id,
      initialValue: {
        tip: item.tip,
        description: item.description,
        channels: channelsToObject(item.communicationChannels),
      },
    });

  const handleFormSubmit = async values => {
    const { mode, suggestionType } = form || {};
    setSavingForm(true);
    try {
      const communicationChannels = channelsToArray(values.channels);
      if (mode === "edit") {
        await updateSuggestion({
          variables: {
            id: Number(form.id),
            tip: values.tip,
            description: values.description,
            communicationChannels,
            opportunityTypeId,
          },
        });
        await refetchTips();
      } else {
        await createSuggestion({
          variables: {
            applicationUseCaseId,
            companyId,
            description: values.description || null,
            isActive: true,
            locationId,
            suggestionType,
            tip: values.tip,
            departmentId: null,
            opportunityTypeId,
            communicationChannels,
          },
        });
        // Refresh the list so the new item shows immediately. Under the `hidden`
        // model a new item isn't in the hidden set, so it's visible by default —
        // no preference write needed.
        await refetchTips();
      }
      setForm(null);
      message.success(mode === "edit" ? "Updated successfully" : "Added successfully");
    } catch (err) {
      message.error(err?.message || "Something went wrong");
    } finally {
      setSavingForm(false);
    }
  };

  // Delete is a two-step flow: the row menu opens a confirmation (pendingDelete),
  // and only confirming actually calls the mutation.
  const confirmDeleteItem = async () => {
    if (!pendingDelete) return;
    const item = pendingDelete;
    setDeleting(true);
    try {
      await deleteSuggestion({ variables: { id: Number(item.id) } });
      await refetchTips();
      onChange({
        ...value,
        quickTipIds: value.quickTipIds.filter(id => id !== item.id),
        toneIds: value.toneIds.filter(id => id !== item.id),
      });
      message.success("Deleted successfully");
      setPendingDelete(null);
    } catch (err) {
      message.error(err?.message || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    if (text?.current) {
      setInputText?.("");
      text.current.placeholder = "Generating response...";
      text.current.value = "";
      text.current.style.height = "0px";
      text.current.focus();
    }
    const promptData = {
      quickTips: textOf(value.quickTipIds, dynamicTips),
      tones: textOf(value.toneIds, dynamicTones),
      additionalNote: value.additionalNote,
    };
    setLoader?.(true);
    let res;
    try {
      res = await getAiResponse({
        variables: buildResponsePayload(promptData),
        fetchPolicy: "network-only",
      });
    } finally {
      setIsGenerating(false);
    }
    dispatch(setTextAiResponseHistoryId(res?.data?.getAiResponse?.aiResponseHistoryId));

    const writeText = res?.data?.getAiResponse?.content;
    if (isEmpty(writeText)) {
      setLoader?.(false);
      closeAll();
      return;
    }
    let index = 0;
    const typeCharacter = () => {
      if (index < writeText.length && !isEmpty(text?.current)) {
        text.current.style.height = `${text.current.scrollHeight}px`;
        text.current.value = isEmpty(text?.current?.value)
          ? writeText.charAt(index)
          : text.current.value + writeText.charAt(index);
        index += 1;
        if (index === writeText.length) {
          setInputText?.(writeText);
          // EAI-1762: remember exactly what Otto wrote, so the draft thumbs
          // can hide once the user edits it.
          dispatch(setTextAiDraftContent(writeText));
          setLoader?.(false);
        }
        setTimeout(typeCharacter, TYPE_DELAY_MS);
      }
    };
    typeCharacter();
    closeAll();
  };

  const panel = hideMode ? (
    <HideUnhidePanel
      isMaximized={maximized}
      onToggleMaximize={toggleMaximize}
      onBack={() => setHideMode(false)}
      loading={quickTipLoader}
      tips={dynamicTips}
      tones={dynamicTones}
      shownTipIds={shownTipIds}
      shownToneIds={shownToneIds}
      onToggleTip={toggleShownTip}
      onToggleTone={toggleShownTone}
      onSave={handleSaveVisibility}
      saving={savingPref}
      additionalCommentsEnabled={additionalCommentsEnabled}
    />
  ) : (
    <CustomizePanelContent
      isMaximized={maximized}
      onToggleMaximize={toggleMaximize}
      onOpenHideUnhide={enterHideMode}
      quickTipLoader={quickTipLoader}
      tips={visibleTips}
      tones={visibleTones}
      emptyTipsText={emptyTipsText}
      emptyTonesText={emptyTonesText}
      value={value}
      onToggleTip={toggleTip}
      onToggleTone={toggleTone}
      onChangeNote={note => onChange({ ...value, additionalNote: note })}
      additionalCommentsEnabled={additionalCommentsEnabled}
      showSavePref={showSavePref}
      savePref={savePref}
      onSavePrefChange={handleSavePrefToggle}
      savingPref={savingPref}
      generating={isGenerating}
      onGenerate={handleGenerate}
      isOwnCustom={item => isOwnCustom(item, userId)}
      onAddTip={() => openAdd(SUGGESTION_TYPE.TIPS)}
      onAddTone={() => openAdd(SUGGESTION_TYPE.TONES)}
      onEditItem={openEdit}
      onDeleteItem={setPendingDelete}
    />
  );

  const deleteNoun = pendingDelete?.suggestionType === SUGGESTION_TYPE.TONES ? "tone" : "tip";
  const deleteNounCap = deleteNoun.charAt(0).toUpperCase() + deleteNoun.slice(1);
  const deleteBody = AI_RESPONSE_COPY.DELETE_CONFIRM_BODY.replace(
    /\{Noun\}/g,
    deleteNounCap
  ).replace(/\{noun\}/g, deleteNoun);

  return (
    <>
      <Popover
        overlayClassName="ai-prompt-popup"
        trigger="click"
        placement="topRight"
        open={open && !maximized}
        overlayInnerStyle={{ padding: 0, overflow: "hidden", borderRadius: 8 }}
        onOpenChange={next => {
          // Keep the popover mounted while a body-portaled Add/Edit form or the
          // delete-confirm modal is open (their clicks would otherwise read as
          // an outside-click that dismisses the panel).
          if (!next && (!!form || !!pendingDelete)) return;
          setOpen(next);
        }}
        content={
          <div
            style={{ width: "100%", maxHeight: "min(90vh, 720px)" }}
            className="flex flex-col overflow-hidden"
          >
            {panel}
          </div>
        }
      >
        {renderTrigger({ open: open && !maximized })}
      </Popover>

      <Drawer
        open={maximized}
        onClose={() => setMaximized(false)}
        placement="right"
        width={400}
        closable={false}
        styles={{ body: { padding: 0, display: "flex", flexDirection: "column" } }}
      >
        {maximized && panel}
      </Drawer>

      {form && (
        <CustomItemForm
          open={!!form}
          mode={form.mode}
          suggestionType={form.suggestionType}
          initialValue={form.initialValue}
          submitting={savingForm}
          channelSelectable={channelSelectable}
          onSubmit={handleFormSubmit}
          onClose={() => setForm(null)}
        />
      )}

      <Modal
        open={!!pendingDelete}
        title={AI_RESPONSE_COPY.DELETE_CONFIRM_TITLE.replace("{noun}", deleteNoun)}
        onCancel={() => setPendingDelete(null)}
        maskClosable={!deleting}
        width={400}
        zIndex={1100}
        footer={
          <div className="flex items-center gap-2">
            <Button danger type="primary" loading={deleting} onClick={confirmDeleteItem}>
              {AI_RESPONSE_COPY.MENU_DELETE}
            </Button>
            <Button disabled={deleting} onClick={() => setPendingDelete(null)}>
              {AI_RESPONSE_COPY.CANCEL}
            </Button>
          </div>
        }
      >
        {deleteBody}
      </Modal>
    </>
  );
}

CustomizeAiResponse.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    company: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) }),
  }).isRequired,
  locationId: PropTypes.number,
  applicationUseCaseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  quickTipVariables: PropTypes.shape({}).isRequired,
  buildResponsePayload: PropTypes.func.isRequired,
  text: PropTypes.shape({}),
  setInputText: PropTypes.func,
  setLoader: PropTypes.func,
  value: PropTypes.shape({
    quickTipIds: PropTypes.arrayOf(PropTypes.number),
    toneIds: PropTypes.arrayOf(PropTypes.number),
    additionalNote: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  emptyTipsText: PropTypes.string,
  emptyTonesText: PropTypes.string,
  channelSelectable: PropTypes.bool,
  renderTrigger: PropTypes.func.isRequired,
};

CustomizeAiResponse.defaultProps = {
  locationId: undefined,
  applicationUseCaseId: undefined,
  text: undefined,
  setInputText: undefined,
  setLoader: undefined,
  emptyTipsText: AI_RESPONSE_COPY.NO_TIPS,
  emptyTonesText: AI_RESPONSE_COPY.NO_TONES,
  channelSelectable: true,
};
